import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, PaperPlaneTilt, Trash, FunnelSimple, Pencil, ArrowsClockwise, Link as LinkIcon, CheckSquare, Square, Checks, UploadSimple, DownloadSimple, FileText, X, CaretUp, CaretDown, CaretUpDown, CalendarPlus, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ImageUpload from "@/components/ImageUpload";
import { useAuth } from "@/contexts/AuthContext";

import { API } from "@/config";
import { DATE_ORDER_OPTIONS, detectDateOrder, previewCsvDate, formatClubDate } from "@/lib/csvDates";
import { RENEW_MONTH_OPTIONS, clubToday, renewalDate, membersToCsv } from "@/lib/renewals";

const Members = () => {
  const { org } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState("all");
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [viewPassesDialogOpen, setViewPassesDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedDesignId, setSelectedDesignId] = useState("");
  const [issuedPasses, setIssuedPasses] = useState([]);
  const [issuingPass, setIssuingPass] = useState(false);
  const [issuingMethod, setIssuingMethod] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkIssuing, setBulkIssuing] = useState(false);
  const [bulkResultOpen, setBulkResultOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const clubDateFormat = org?.date_format || detectDateOrder();
  const [csvDateFormat, setCsvDateFormat] = useState(clubDateFormat);
  useEffect(() => { setCsvDateFormat(clubDateFormat); }, [clubDateFormat]);
  const [csvClub, setCsvClub] = useState("");
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [trialLimitOpen, setTrialLimitOpen] = useState(false);
  const [trialLimitMessage, setTrialLimitMessage] = useState("");
  const fileInputRef = useRef(null);
  
  const [newMember, setNewMember] = useState({
    club_name: "",
    member_name: "",
    member_id: "",
    member_email: "",
    membership_status: "Active",
    expiry_date: "",
    photo_url: "",
  });

  useEffect(() => {
    fetchMembers();
    fetchDesigns();
    fetchClubs();
  }, []);

  useEffect(() => {
    if (selectedClub === "all") {
      fetchMembers();
    } else {
      fetchMembersByClub(selectedClub);
    }
    setSelectedIds(new Set());
  }, [selectedClub]);

  // Default the Add Member dialog's club to the org's own name, so people
  // aren't forced to pick it every time — still fully editable/selectable.
  useEffect(() => {
    if (addDialogOpen && !newMember.club_name && org?.name) {
      setNewMember((prev) => ({ ...prev, club_name: org.name }));
    }
  }, [addDialogOpen, org, newMember.club_name]);

  useEffect(() => {
    if (csvDialogOpen && !csvClub && org?.name) {
      setCsvClub(org.name);
    }
  }, [csvDialogOpen, org, csvClub]);

  // Club options for both Select dropdowns — includes the org's own name
  // even before any member/design has used it yet (e.g. a brand new org).
  const clubOptions = org?.name && !clubs.includes(org.name) ? [org.name, ...clubs] : clubs;

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to fetch members");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembersByClub = async (clubName) => {
    try {
      const response = await axios.get(`${API}/members/by-club/${encodeURIComponent(clubName)}`);
      setMembers(response.data);
    } catch (error) {
      console.error("Error fetching members by club:", error);
      toast.error("Failed to fetch members");
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await axios.get(`${API}/clubs`);
      setClubs(response.data.clubs || []);
    } catch (error) {
      console.error("Error fetching clubs:", error);
    }
  };

  const fetchDesigns = async () => {
    try {
      const response = await axios.get(`${API}/pass-designs`);
      setDesigns(response.data);
    } catch (error) {
      console.error("Error fetching designs:", error);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.club_name || !newMember.member_name || !newMember.member_id || !newMember.member_email) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const memberData = { ...newMember };
      if (memberData.expiry_date) {
        memberData.expiry_date = new Date(memberData.expiry_date).toISOString();
      } else {
        delete memberData.expiry_date;
      }
      if (!memberData.photo_url) {
        delete memberData.photo_url;
      }
      
      await axios.post(`${API}/members`, memberData);
      toast.success("Member added successfully!");
      setAddDialogOpen(false);
      setNewMember({
        club_name: "",
        member_name: "",
        member_id: "",
        member_email: "",
        membership_status: "Active",
        expiry_date: "",
        photo_url: "",
      });
      fetchMembers();
      fetchClubs();
    } catch (error) {
      console.error("Error adding member:", error);
      toast.error("Failed to add member");
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    try {
      await axios.delete(`${API}/members/${memberId}`);
      toast.success(`"${memberName}" deleted successfully!`);
      fetchMembers();
      fetchClubs();
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error("Failed to delete member");
    }
  };

  const handleEditMember = (member) => {
    setEditingMember({
      ...member,
      // expiry_date is a calendar date — take it as written (YYYY-MM-DD) rather
      // than via Date(), which shifts it a day depending on the browser's zone.
      expiry_date: member.expiry_date ? String(member.expiry_date).slice(0, 10) : "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateMember = async () => {
    if (!editingMember.club_name || !editingMember.member_name || !editingMember.member_id || !editingMember.member_email) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const updateData = { ...editingMember };
      if (updateData.expiry_date) {
        updateData.expiry_date = new Date(updateData.expiry_date).toISOString();
      } else {
        delete updateData.expiry_date;
      }
      if (!updateData.photo_url) {
        delete updateData.photo_url;
      }
      delete updateData.id;
      delete updateData.created_at;
      
      await axios.put(`${API}/members/${editingMember.id}`, updateData);
      toast.success("Member updated successfully!");
      setEditDialogOpen(false);
      setEditingMember(null);
      fetchMembers();
      fetchClubs();
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error("Failed to update member");
    }
  };

  const handleSyncPasses = async (memberId, memberName) => {
    try {
      const response = await axios.post(`${API}/sync-pass`, { member_id: memberId });
      toast.success(`Synced ${response.data.synced_passes} pass(es) for "${memberName}"!`);
    } catch (error) {
      console.error("Error syncing passes:", error);
      if (error.response?.status === 404) {
        toast.error("No passes found for this member");
      } else {
        toast.error("Failed to sync passes");
      }
    }
  };

  const handleViewPasses = async (member) => {
    setSelectedMember(member);
    try {
      const response = await axios.get(`${API}/issued-passes/${member.id}`);
      setIssuedPasses(response.data.passes || []);
      setViewPassesDialogOpen(true);
    } catch (error) {
      console.error("Error fetching issued passes:", error);
      toast.error("Failed to load issued passes");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard!");
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredMembers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMembers.map((m) => m.id)));
    }
  };

  const handleBulkIssue = async () => {
    setBulkIssuing(true);
    try {
      const response = await axios.post(`${API}/issue-passes/bulk`, {
        member_ids: Array.from(selectedIds),
      });
      setBulkResult(response.data);
      setBulkConfirmOpen(false);
      setBulkResultOpen(true);
      setSelectedIds(new Set());
      if (response.data.issued > 0) {
        toast.success(`${response.data.issued} pass(es) issued successfully!`);
      }
    } catch (error) {
      console.error("Error in bulk issue:", error);
      toast.error("Bulk pass issuance failed");
    } finally {
      setBulkIssuing(false);
    }
  };

  const handleCsvFileSelect = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }
    setCsvFile(file);
    setCsvResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target.result).replace(/^\uFEFF/, ""); // Excel/export BOM
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) {
        toast.error("CSV file has no data rows");
        setCsvPreview([]);
        return;
      }
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const required = ["member_name", "member_id", "member_email"];
      const missing = required.filter((r) => !headers.includes(r));
      if (missing.length > 0) {
        toast.error(`CSV missing columns: ${missing.join(", ")}`);
        setCsvPreview([]);
        return;
      }
      const rows = [];
      for (let i = 1; i < Math.min(lines.length, 101); i++) {
        const values = lines[i].split(",").map((v) => v.trim());
        const row = {};
        headers.forEach((h, idx) => { row[h] = values[idx] || ""; });
        rows.push(row);
      }
      setCsvPreview(rows);
    };
    reader.readAsText(file);
  };

  const handleCsvImport = async () => {
    if (!csvFile || !csvClub) {
      toast.error("Please select a club and CSV file");
      return;
    }
    setCsvImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      const response = await axios.post(
        `${API}/members/import-csv?club_name=${encodeURIComponent(csvClub)}&date_format=${csvDateFormat}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setCsvResult(response.data);
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(`Imported: ${response.data.created} new, ${response.data.updated} updated`);
      }
      fetchMembers();
      fetchClubs();
    } catch (error) {
      console.error("CSV import failed:", error);
      toast.error(error.response?.data?.detail || "CSV import failed");
    } finally {
      setCsvImporting(false);
    }
  };

  const handleDownloadCsvTemplate = () => {
    // Exact columns members/import-csv accepts (see backend/server.py) —
    // member_name/member_id/member_email are required, the rest optional.
    // Plain CSV rather than a real Google Sheets file: opens directly in
    // Google Sheets, Excel, or Numbers with no extra setup.
    const headers = ["member_name", "member_id", "member_email", "membership_status", "expiry_date", "photo_url"];
    const exampleRows = [
      ["Alex Morgan", "M-1001", "alex@example.com", "Active", "2027-03-31", ""],
      ["Jamie Lee", "M-1002", "jamie@example.com", "Active", "2027-06-15", ""],
    ];
    const csv = [headers, ...exampleRows].map((row) => row.join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "club-pass-members-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resetCsvDialog = () => {
    setCsvFile(null);
    setCsvClub("");
    setCsvPreview([]);
    setCsvResult(null);
    setCsvDialogOpen(false);
  };

  const handleIssuePass = async (sendEmail = false) => {
    if (!selectedDesignId) {
      toast.error("Please select a pass design");
      return;
    }

    setIssuingPass(true);
    setIssuingMethod(sendEmail ? "email" : "link");
    try {
      const response = await axios.post(`${API}/issue-pass`, {
        member_id: selectedMember.id,
        pass_design_id: selectedDesignId,
        send_email: sendEmail,
      });

      const walletLink = response.data.wallet_link;

      setIssueDialogOpen(false);
      setSelectedMember(null);
      setSelectedDesignId("");

      if (sendEmail) {
        toast.success(`Pass issued and email sent to ${response.data.member_email}!`);
      } else {
        navigator.clipboard.writeText(walletLink);
        toast.success("Pass issued! Link copied to clipboard.");
      }
    } catch (error) {
      console.error("Error issuing pass:", error);
      if (error.response?.status === 402) {
        setIssueDialogOpen(false);
        setTrialLimitMessage(error.response?.data?.detail || "Trial pass limit reached. Subscribe to a plan to issue more.");
        setTrialLimitOpen(true);
      } else {
        toast.error(error.response?.data?.detail || "Failed to issue pass");
      }
    } finally {
      setIssuingPass(false);
      setIssuingMethod(null);
    }
  };

  const openIssueDialog = async (member) => {
    setSelectedMember(member);
    setIssueDialogOpen(true);
    
    try {
      const response = await axios.get(`${API}/pass-designs/by-club/${encodeURIComponent(member.club_name)}`);
      if (response.data.found && response.data.id) {
        setSelectedDesignId(response.data.id);
      } else {
        setSelectedDesignId("");
      }
    } catch (error) {
      console.error("Error finding matching design:", error);
      setSelectedDesignId("");
    }
  };

  // Clickable column sorting. Clicking a new column sorts ascending; clicking
  // the same column again flips direction. Members with no expiry date always
  // sort last.
  const [sort, setSort] = useState({ key: "member_name", dir: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [renewTargets, setRenewTargets] = useState(null); // members being renewed; null = dialog closed
  const [renewMonths, setRenewMonths] = useState("12");
  const [renewing, setRenewing] = useState(false);
  const renewMonthsNum = Math.max(1, Math.min(60, parseInt(renewMonths, 10) || 0));
  const today = clubToday(org?.timezone || "Pacific/Auckland");

  const handleRenew = async () => {
    if (!renewTargets?.length) return;
    setRenewing(true);
    try {
      const { data } = await axios.post(`${API}/members/renew`, {
        member_ids: renewTargets.map((m) => m.id),
        months: renewMonthsNum,
      });
      const passNote = data.passes_synced ? ` · ${data.passes_synced} wallet pass${data.passes_synced === 1 ? "" : "es"} updated` : "";
      toast.success(`Renewed ${data.renewed} member${data.renewed === 1 ? "" : "s"} by ${renewMonthsNum} month${renewMonthsNum === 1 ? "" : "s"}${passNote}`);
      if (data.pass_errors) toast.error(`${data.pass_errors} member(s) renewed but their wallet pass didn't update — try Sync`);
      setRenewTargets(null);
      setSelectedIds(new Set());
      selectedClub === "all" ? fetchMembers() : fetchMembersByClub(selectedClub);
    } catch (error) {
      console.error("Renew failed:", error);
      toast.error(error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || "Renewal failed");
    } finally {
      setRenewing(false);
    }
  };

  const handleExportCsv = () => {
    const csv = membersToCsv(filteredMembers);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }); // BOM so Excel reads UTF-8 names
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `members-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const q = searchQuery.trim().toLowerCase();
  const matchesSearch = (m) =>
    !q || [m.member_name, m.member_id, m.member_email].some((v) => String(v || "").toLowerCase().includes(q));
  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sortValue = (member, key) => {
    const v = member[key];
    if (key === "expiry_date") return v ? String(v).slice(0, 10) : null; // YYYY-MM-DD sorts correctly as text
    return v == null ? "" : String(v);
  };

  const filteredMembers = members.filter(matchesSearch).sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    if (av === null || bv === null) {
      if (av === bv) return 0;
      return av === null ? 1 : -1; // blanks last in both directions
    }
    // numeric-aware so M-2 comes before M-10
    const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const SortableHead = ({ columnKey, children, className = "" }) => {
    const active = sort.key === columnKey;
    const Icon = !active ? CaretUpDown : sort.dir === "asc" ? CaretUp : CaretDown;
    return (
      <TableHead className={className} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
        <button
          type="button"
          onClick={() => toggleSort(columnKey)}
          className={`inline-flex items-center gap-1 hover:text-zinc-900 ${active ? "text-zinc-900" : ""}`}
          data-testid={`sort-${columnKey}`}
        >
          {children}
          <Icon size={14} weight="bold" className={active ? "" : "text-zinc-400"} />
        </button>
      </TableHead>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12" data-testid="members-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900">
            Members
          </h1>
          <p className="text-base text-zinc-600 mt-2">
            Manage members across all your clubs
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleExportCsv}
            variant="outline"
            disabled={filteredMembers.length === 0}
            title="Download the members currently shown (search & filter apply) — re-importable"
            data-testid="export-csv-btn"
          >
            <DownloadSimple size={20} weight="bold" className="mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => setCsvDialogOpen(true)}
            variant="outline"
            className="border-[var(--brand)] text-[var(--brand)] hover:bg-[var(--brand-tint-5)]"
            data-testid="import-csv-btn"
          >
            <UploadSimple size={20} weight="bold" className="mr-2" />
            Import CSV
          </Button>
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                data-testid="add-member-btn"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Add Member
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" data-testid="add-member-dialog">
            <DialogHeader>
              <DialogTitle>Add New Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="club">Club Name *</Label>
                <Select
                  value={newMember.club_name}
                  onValueChange={(val) => setNewMember({ ...newMember, club_name: val })}
                >
                  <SelectTrigger className="mt-2" data-testid="member-club-input">
                    <SelectValue placeholder="Select a club" />
                  </SelectTrigger>
                  <SelectContent>
                    {clubOptions.map((club) => (
                      <SelectItem key={club} value={club}>{club}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="name">Member Name *</Label>
                <Input
                  id="name"
                  data-testid="member-name-input"
                  value={newMember.member_name}
                  onChange={(e) => setNewMember({ ...newMember, member_name: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="id">Member ID *</Label>
                <Input
                  id="id"
                  data-testid="member-id-input"
                  value={newMember.member_id}
                  onChange={(e) => setNewMember({ ...newMember, member_id: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="member-email-input"
                  value={newMember.member_email}
                  onChange={(e) => setNewMember({ ...newMember, member_email: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={newMember.membership_status}
                  onValueChange={(value) => setNewMember({ ...newMember, membership_status: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="member-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Suspended">Suspended</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="expiry">Expiry Date</Label>
                <Input
                  id="expiry"
                  type="date"
                  data-testid="member-expiry-input"
                  value={newMember.expiry_date}
                  onChange={(e) => setNewMember({ ...newMember, expiry_date: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Member Photo</Label>
                <div className="mt-2">
                  <ImageUpload
                    value={newMember.photo_url}
                    onChange={(url) => setNewMember({ ...newMember, photo_url: url })}
                    label="Photo"
                    placeholder="Upload or paste photo URL"
                    compact
                  />
                </div>
              </div>
              <Button
                onClick={handleAddMember}
                className="w-full bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                data-testid="submit-member-btn"
              >
                Add Member
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2">
            <FunnelSimple size={20} className="text-zinc-600" />
            <Label className="text-sm font-medium">Filter by Club:</Label>
          </div>
          <Select value={selectedClub} onValueChange={setSelectedClub}>
            <SelectTrigger className="w-full sm:w-64" data-testid="club-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clubs</SelectItem>
              {clubs.map((club) => (
                <SelectItem key={club} value={club}>
                  {club}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, ID or email"
              className="pl-9 pr-8"
              data-testid="member-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                aria-label="Clear search"
              >
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
        </div>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 bg-[var(--brand-tint-5)] border border-[var(--brand-tint-20)] rounded-md px-4 py-2" data-testid="bulk-action-bar">
            <span className="text-sm font-medium text-[var(--brand)]" data-testid="bulk-selected-count">
              {selectedIds.size} selected
            </span>
            <Button
              size="sm"
              className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
              onClick={() => setBulkConfirmOpen(true)}
              data-testid="bulk-issue-btn"
            >
              <Checks size={16} weight="bold" className="mr-1" />
              Issue {selectedIds.size} Pass{selectedIds.size > 1 ? "es" : ""}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-[var(--brand)] text-[var(--brand)]"
              onClick={() => setRenewTargets(members.filter((m) => selectedIds.has(m.id)))}
              data-testid="bulk-renew-btn"
            >
              <CalendarPlus size={16} weight="bold" className="mr-1" />
              Renew {selectedIds.size}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-zinc-500 hover:text-zinc-700"
              onClick={() => setSelectedIds(new Set())}
              data-testid="bulk-clear-btn"
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      <div className="border border-zinc-200 bg-white rounded-md">
        {loading ? (
          <div className="p-12 text-center text-zinc-500">Loading...</div>
        ) : filteredMembers.length === 0 ? (
          <div className="p-12 text-center text-zinc-500" data-testid="no-members-message">
            No members found. Add your first member to get started.
          </div>
        ) : (
          <Table data-testid="members-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center justify-center"
                    data-testid="select-all-checkbox"
                  >
                    {selectedIds.size === filteredMembers.length && filteredMembers.length > 0 ? (
                      <CheckSquare size={20} weight="fill" className="text-[var(--brand)]" />
                    ) : (
                      <Square size={20} weight="regular" className="text-zinc-400" />
                    )}
                  </button>
                </TableHead>
                <SortableHead columnKey="member_name">Name</SortableHead>
                <SortableHead columnKey="member_id">Member ID</SortableHead>
                <SortableHead columnKey="member_email">Email</SortableHead>
                <SortableHead columnKey="membership_status">Status</SortableHead>
                <SortableHead columnKey="expiry_date">Expiry</SortableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.map((member, index) => (
                <TableRow
                  key={member.id}
                  data-testid={`member-row-${index}`}
                  className={selectedIds.has(member.id) ? "bg-[var(--brand-tint-5)]" : ""}
                >
                  <TableCell>
                    <button
                      onClick={() => toggleSelect(member.id)}
                      className="flex items-center justify-center"
                      data-testid={`select-member-${index}`}
                    >
                      {selectedIds.has(member.id) ? (
                        <CheckSquare size={20} weight="fill" className="text-[var(--brand)]" />
                      ) : (
                        <Square size={20} weight="regular" className="text-zinc-400" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium">{member.member_name}</TableCell>
                  <TableCell>{member.member_id}</TableCell>
                  <TableCell>{member.member_email}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        member.membership_status === "Active"
                          ? "bg-green-100 text-green-800"
                          : member.membership_status === "Expired"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {member.membership_status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {member.expiry_date
                      ? formatClubDate(member.expiry_date, clubDateFormat)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => handleViewPasses(member)}
                        size="sm"
                        variant="outline"
                        className="border-purple-200 text-purple-600 hover:bg-purple-50"
                        data-testid={`view-passes-btn-${index}`}
                        title="View issued passes & links"
                      >
                        <LinkIcon size={16} weight="bold" />
                      </Button>
                      <Button
                        onClick={() => handleEditMember(member)}
                        size="sm"
                        variant="outline"
                        className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        data-testid={`edit-member-btn-${index}`}
                      >
                        <Pencil size={16} weight="bold" />
                      </Button>
                      <Button
                        onClick={() => openIssueDialog(member)}
                        size="sm"
                        className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                        data-testid={`issue-pass-btn-${index}`}
                      >
                        <PaperPlaneTilt size={16} weight="bold" className="mr-1" />
                        Issue Pass
                      </Button>
                      <Button
                        onClick={() => setRenewTargets([member])}
                        size="sm"
                        variant="outline"
                        className="border-amber-200 text-amber-700 hover:bg-amber-50"
                        data-testid={`renew-member-btn-${index}`}
                        title="Renew membership"
                      >
                        <CalendarPlus size={16} weight="bold" />
                      </Button>
                      <Button
                        onClick={() => handleSyncPasses(member.id, member.member_name)}
                        size="sm"
                        variant="outline"
                        className="border-green-200 text-green-600 hover:bg-green-50"
                        data-testid={`sync-pass-btn-${index}`}
                        title="Sync changes to issued passes"
                      >
                        <ArrowsClockwise size={16} weight="bold" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteMember(member.id, member.member_name)}
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`delete-member-btn-${index}`}
                      >
                        <Trash size={16} weight="bold" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
        <DialogContent data-testid="issue-pass-dialog">
          <DialogHeader>
            <DialogTitle>Issue Pass to {selectedMember?.member_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
              <p className="text-sm text-blue-900 font-medium">
                Member Club: {selectedMember?.club_name}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                The system will auto-select the matching pass design
              </p>
            </div>
            <div>
              <Label htmlFor="design">Select Pass Design</Label>
              <Select value={selectedDesignId} onValueChange={setSelectedDesignId}>
                <SelectTrigger className="mt-2" data-testid="design-select">
                  <SelectValue placeholder="Choose a design" />
                </SelectTrigger>
                <SelectContent>
                  {designs.map((design) => (
                    <SelectItem key={design.id} value={design.id}>
                      {design.club_name} {design.club_name === selectedMember?.club_name ? "✓ (Matched)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-zinc-50 p-4 rounded-md border border-zinc-200">
              <p className="text-sm text-zinc-600">
                The pass will be created in Google Wallet for:
              </p>
              <p className="text-sm font-medium text-zinc-900 mt-2">
                {selectedMember?.member_email}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => handleIssuePass(true)}
                className="flex-1 bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white disabled:opacity-70"
                disabled={issuingPass}
                data-testid="issue-via-email-btn"
              >
                {issuingPass && issuingMethod === "email" ? (
                  <ArrowsClockwise size={16} weight="bold" className="mr-2 animate-spin" />
                ) : (
                  <PaperPlaneTilt size={16} weight="bold" className="mr-2" />
                )}
                {issuingPass && issuingMethod === "email" ? "Sending..." : "Send via Email"}
              </Button>
              <Button
                onClick={() => handleIssuePass(false)}
                variant="outline"
                className="flex-1 disabled:opacity-70"
                disabled={issuingPass}
                data-testid="issue-via-link-btn"
              >
                {issuingPass && issuingMethod === "link" ? (
                  <ArrowsClockwise size={16} weight="bold" className="mr-2 animate-spin" />
                ) : (
                  <LinkIcon size={16} weight="bold" className="mr-2" />
                )}
                {issuingPass && issuingMethod === "link" ? "Generating..." : "Get Link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={trialLimitOpen} onOpenChange={setTrialLimitOpen}>
        <DialogContent className="max-w-md text-center" data-testid="trial-limit-dialog">
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold text-center mx-auto">
              Trial Limit Reached
            </DialogTitle>
          </DialogHeader>
          <p className="text-xl leading-snug text-zinc-700 mt-2">
            {trialLimitMessage}
          </p>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setTrialLimitOpen(false)}
              data-testid="trial-limit-dismiss-btn"
            >
              Dismiss
            </Button>
            <Button
              className="flex-1 bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
              onClick={() => {
                setTrialLimitOpen(false);
                navigate("/settings/billing");
              }}
              data-testid="trial-limit-upgrade-btn"
            >
              View Plans
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="edit-member-dialog">
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
          </DialogHeader>
          {editingMember && (
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-club">Club Name *</Label>
                <Select
                  value={editingMember.club_name}
                  onValueChange={(val) => setEditingMember({ ...editingMember, club_name: val })}
                >
                  <SelectTrigger className="mt-2" data-testid="edit-member-club-input">
                    <SelectValue placeholder="Select a club" />
                  </SelectTrigger>
                  <SelectContent>
                    {clubOptions.map((club) => (
                      <SelectItem key={club} value={club}>{club}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-name">Member Name *</Label>
                <Input
                  id="edit-name"
                  data-testid="edit-member-name-input"
                  value={editingMember.member_name}
                  onChange={(e) => setEditingMember({ ...editingMember, member_name: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="edit-id">Member ID *</Label>
                <Input
                  id="edit-id"
                  data-testid="edit-member-id-input"
                  value={editingMember.member_id}
                  onChange={(e) => setEditingMember({ ...editingMember, member_id: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  data-testid="edit-member-email-input"
                  value={editingMember.member_email}
                  onChange={(e) => setEditingMember({ ...editingMember, member_email: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={editingMember.membership_status}
                  onValueChange={(value) => setEditingMember({ ...editingMember, membership_status: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="edit-member-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Suspended">Suspended</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-expiry">Expiry Date</Label>
                <Input
                  id="edit-expiry"
                  type="date"
                  data-testid="edit-member-expiry-input"
                  value={editingMember.expiry_date}
                  onChange={(e) => setEditingMember({ ...editingMember, expiry_date: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Member Photo</Label>
                <div className="mt-2">
                  <ImageUpload
                    value={editingMember.photo_url || ""}
                    onChange={(url) => setEditingMember({ ...editingMember, photo_url: url })}
                    label="Edit Photo"
                    placeholder="Upload or paste photo URL"
                    compact
                  />
                </div>
              </div>
              <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                <p className="text-xs text-amber-900">
                  💡 After updating, click the <ArrowsClockwise size={14} className="inline" weight="bold" /> Sync button to push changes to issued passes
                </p>
              </div>
              <Button
                onClick={handleUpdateMember}
                className="w-full bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                data-testid="update-member-btn"
              >
                Update Member
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={viewPassesDialogOpen} onOpenChange={setViewPassesDialogOpen}>
        <DialogContent className="sm:max-w-[700px]" data-testid="view-passes-dialog">
          <DialogHeader>
            <DialogTitle>Issued Passes for {selectedMember?.member_name}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {issuedPasses.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No passes issued yet for this member
              </div>
            ) : (
              <div className="space-y-4">
                {issuedPasses.map((pass, index) => (
                  <div
                    key={pass.id}
                    className="border border-zinc-200 rounded-md p-4 bg-zinc-50"
                    data-testid={`pass-item-${index}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-zinc-900">
                          Pass #{index + 1}
                        </p>
                        <p className="text-xs text-zinc-500 mt-1">
                          Issued: {new Date(pass.issued_at).toLocaleString()}
                        </p>
                        {pass.is_expired && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                            Expired
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="bg-white border border-zinc-200 rounded p-3 mt-3">
                      <Label className="text-xs text-zinc-600">Google Wallet Link</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          type="text"
                          value={pass.wallet_link}
                          readOnly
                          className="text-xs border rounded px-3 py-2 w-full bg-white font-mono"
                          onClick={(e) => e.target.select()}
                        />
                        <Button
                          size="sm"
                          onClick={() => copyToClipboard(pass.wallet_link)}
                          className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white whitespace-nowrap"
                          data-testid={`copy-link-btn-${index}`}
                        >
                          <LinkIcon size={16} weight="bold" className="mr-1" />
                          Copy
                        </Button>
                      </div>
                      <p className="text-xs text-zinc-500 mt-2">
                        Share this link with the member to add the pass to Google Wallet
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={!!renewTargets} onOpenChange={(open) => !open && !renewing && setRenewTargets(null)}>
        <DialogContent data-testid="renew-dialog">
          <DialogHeader>
            <DialogTitle>
              Renew {renewTargets?.length === 1 ? renewTargets[0].member_name : `${renewTargets?.length || 0} members`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Extend by</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {RENEW_MONTH_OPTIONS.map((n) => (
                  <Button
                    key={n}
                    type="button"
                    size="sm"
                    variant={renewMonthsNum === n ? "default" : "outline"}
                    className={renewMonthsNum === n ? "bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white" : ""}
                    onClick={() => setRenewMonths(String(n))}
                  >
                    {n} month{n === 1 ? "" : "s"}
                  </Button>
                ))}
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={renewMonths}
                    onChange={(e) => setRenewMonths(e.target.value)}
                    className="w-20 h-9"
                    data-testid="renew-months-input"
                  />
                  <span className="text-sm text-zinc-500">months</span>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                Extends from the current expiry date if it's still valid, otherwise from today. Renewed members become
                Active (Suspended members stay Suspended) and their wallet passes update automatically.
              </p>
            </div>
            <div className="border border-zinc-200 rounded-md max-h-60 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Member</TableHead>
                    <TableHead className="text-xs">Current</TableHead>
                    <TableHead className="text-xs">New expiry</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(renewTargets || []).map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="text-xs">{m.member_name}</TableCell>
                      <TableCell className="text-xs text-zinc-500">
                        {m.expiry_date ? formatClubDate(m.expiry_date, clubDateFormat) : "—"}
                      </TableCell>
                      <TableCell className="text-xs font-medium">
                        {formatClubDate(renewalDate(m.expiry_date, today, renewMonthsNum), clubDateFormat)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRenewTargets(null)} disabled={renewing}>Cancel</Button>
              <Button
                className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                onClick={handleRenew}
                disabled={renewing || !(parseInt(renewMonths, 10) >= 1)}
                data-testid="renew-confirm-btn"
              >
                {renewing ? "Renewing..." : `Renew ${renewMonthsNum} month${renewMonthsNum === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Issue Confirmation Dialog */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Bulk Pass Issuance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
              <p className="text-sm text-blue-900 font-medium" data-testid="bulk-confirm-message">
                You are about to issue {selectedIds.size} pass{selectedIds.size > 1 ? "es" : ""} to {selectedIds.size} member{selectedIds.size > 1 ? "s" : ""}.
              </p>
              <p className="text-xs text-blue-700 mt-2">
                Each member will be auto-matched to their club's pass design. Members without a matching design will be skipped.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setBulkConfirmOpen(false)}
                data-testid="bulk-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                onClick={handleBulkIssue}
                disabled={bulkIssuing}
                data-testid="bulk-confirm-issue-btn"
              >
                {bulkIssuing ? "Issuing..." : `Issue ${selectedIds.size} Pass${selectedIds.size > 1 ? "es" : ""}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Issue Results Dialog */}
      <Dialog open={bulkResultOpen} onOpenChange={setBulkResultOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" data-testid="bulk-result-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Issuance Results</DialogTitle>
          </DialogHeader>
          {bulkResult && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center" data-testid="bulk-issued-count">
                  <p className="text-2xl font-bold text-green-700">{bulkResult.issued}</p>
                  <p className="text-xs text-green-600">Issued</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-center" data-testid="bulk-skipped-count">
                  <p className="text-2xl font-bold text-amber-700">{bulkResult.skipped}</p>
                  <p className="text-xs text-amber-600">Skipped</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-center" data-testid="bulk-failed-count">
                  <p className="text-2xl font-bold text-red-700">{bulkResult.failed}</p>
                  <p className="text-xs text-red-600">Failed</p>
                </div>
              </div>

              {bulkResult.skipped_members.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-700 mb-2">Skipped Members</h4>
                  <div className="space-y-2">
                    {bulkResult.skipped_members.map((s, i) => (
                      <div key={i} className="bg-amber-50 border border-amber-200 rounded p-2 text-sm" data-testid={`skipped-member-${i}`}>
                        <span className="font-medium">{s.member_name || s.member_id}</span>
                        {s.club_name && <span className="text-zinc-500"> ({s.club_name})</span>}
                        <span className="text-amber-700 block text-xs mt-0.5">{s.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bulkResult.results.filter(r => r.status === "issued").length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-700 mb-2">Issued Passes</h4>
                  <div className="space-y-2">
                    {bulkResult.results.filter(r => r.status === "issued").map((r, i) => (
                      <div key={i} className="bg-green-50 border border-green-200 rounded p-2" data-testid={`issued-result-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-green-800">{r.member_name}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-green-300 text-green-700 hover:bg-green-100"
                            onClick={() => copyToClipboard(r.wallet_link)}
                            data-testid={`copy-bulk-link-${i}`}
                          >
                            <LinkIcon size={14} weight="bold" className="mr-1" />
                            Copy Link
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bulkResult.results.filter(r => r.status === "failed").length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-700 mb-2">Failed</h4>
                  <div className="space-y-2">
                    {bulkResult.results.filter(r => r.status === "failed").map((r, i) => (
                      <div key={i} className="bg-red-50 border border-red-200 rounded p-2 text-sm" data-testid={`failed-result-${i}`}>
                        <span className="font-medium text-red-800">{r.member_name}</span>
                        <span className="text-red-600 block text-xs mt-0.5">{r.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                className="w-full"
                variant="outline"
                onClick={() => setBulkResultOpen(false)}
                data-testid="bulk-result-close-btn"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={(open) => { if (!open) resetCsvDialog(); else setCsvDialogOpen(true); }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Members from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {!csvResult ? (
              <>
                <button
                  type="button"
                  onClick={handleDownloadCsvTemplate}
                  className="flex items-center gap-2 text-sm text-[var(--brand)] hover:underline"
                  data-testid="download-csv-template-btn"
                >
                  <DownloadSimple size={16} weight="bold" />
                  Download CSV template
                </button>

                <div>
                  <Label>Assign to Club *</Label>
                  <Select value={csvClub} onValueChange={setCsvClub}>
                    <SelectTrigger className="mt-2" data-testid="csv-club-select">
                      <SelectValue placeholder="Select a club" />
                    </SelectTrigger>
                    <SelectContent>
                      {clubOptions.map((club) => (
                        <SelectItem key={club} value={club}>{club}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Date format in this file</Label>
                  <Select value={csvDateFormat} onValueChange={setCsvDateFormat}>
                    <SelectTrigger className="mt-2" data-testid="csv-date-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_ORDER_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-400 mt-1">Defaults to your club's date format (Account settings). Dates like 2027-03-14 or 14 Mar 2027 work with any setting.</p>
                </div>

                <div>
                  <Label>CSV File *</Label>
                  <div
                    className="mt-2 border-2 border-dashed border-zinc-300 rounded-lg p-6 text-center hover:border-[var(--brand-tint-50)] transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleCsvFileSelect(e.dataTransfer.files[0]); }}
                    data-testid="csv-dropzone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={(e) => handleCsvFileSelect(e.target.files[0])}
                      data-testid="csv-file-input"
                    />
                    {csvFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileText size={20} className="text-[var(--brand)]" />
                        <span className="text-sm font-medium text-zinc-700">{csvFile.name}</span>
                        <button onClick={(e) => { e.stopPropagation(); setCsvFile(null); setCsvPreview([]); }} className="ml-2 text-zinc-400 hover:text-red-500">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <UploadSimple size={28} className="mx-auto text-zinc-400 mb-2" />
                        <p className="text-sm text-zinc-500">Click or drag a CSV file here</p>
                        <p className="text-xs text-zinc-400 mt-1">Required columns: member_name, member_id, member_email</p>
                        <p className="text-xs text-zinc-400">Optional: membership_status, expiry_date, photo_url</p>
                      </div>
                    )}
                  </div>
                </div>

                {csvPreview.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">Preview ({csvPreview.length} row{csvPreview.length !== 1 ? "s" : ""})</Label>
                      {csvPreview.length >= 100 && <span className="text-xs text-zinc-400">Showing first 100 rows</span>}
                    </div>
                    <div className="border border-zinc-200 rounded-md overflow-x-auto max-h-[300px] overflow-y-auto">
                      <Table data-testid="csv-preview-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Name</TableHead>
                            <TableHead className="text-xs">Member ID</TableHead>
                            <TableHead className="text-xs">Email</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs">Expiry</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {csvPreview.slice(0, 20).map((row, i) => (
                            <TableRow key={i} data-testid={`csv-preview-row-${i}`}>
                              <TableCell className="text-xs">{row.member_name}</TableCell>
                              <TableCell className="text-xs">{row.member_id}</TableCell>
                              <TableCell className="text-xs">{row.member_email}</TableCell>
                              <TableCell className="text-xs">{row.membership_status || "Active"}</TableCell>
                              <TableCell className="text-xs">
                                {(() => {
                                  if (!row.expiry_date) return "—";
                                  const p = previewCsvDate(row.expiry_date, csvDateFormat);
                                  if (!p.ok) return <span className="text-red-600 font-medium" title="This row will be rejected">{row.expiry_date} — invalid</span>;
                                  return (
                                    <span title={`From "${row.expiry_date}"`}>
                                      {p.date.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}
                                    </span>
                                  );
                                })()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div className="bg-zinc-50 p-3 rounded-md border border-zinc-200">
                  <p className="text-xs text-zinc-600">
                    Members with a matching <span className="font-medium">member_id</span> in the selected club will be <span className="font-medium">updated</span>. New members will be created.
                  </p>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={resetCsvDialog} data-testid="csv-cancel-btn">Cancel</Button>
                  <Button
                    onClick={handleCsvImport}
                    disabled={!csvFile || !csvClub || csvImporting}
                    className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                    data-testid="csv-import-submit-btn"
                  >
                    {csvImporting ? "Importing..." : `Import ${csvPreview.length} Member${csvPreview.length !== 1 ? "s" : ""}`}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4" data-testid="csv-result-panel">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center" data-testid="csv-created-count">
                    <p className="text-2xl font-bold text-green-700">{csvResult.created}</p>
                    <p className="text-xs text-green-600">Created</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-center" data-testid="csv-updated-count">
                    <p className="text-2xl font-bold text-blue-700">{csvResult.updated}</p>
                    <p className="text-xs text-blue-600">Updated</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 text-center" data-testid="csv-failed-count">
                    <p className="text-2xl font-bold text-red-700">{csvResult.failed}</p>
                    <p className="text-xs text-red-600">Failed</p>
                  </div>
                </div>

                {csvResult.errors && csvResult.errors.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-red-700">Errors</Label>
                    <div className="mt-2 space-y-1 max-h-[150px] overflow-y-auto">
                      {csvResult.errors.map((err, i) => (
                        <div key={i} className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700" data-testid={`csv-error-${i}`}>
                          Row {err.row}: {err.reason}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button className="w-full" variant="outline" onClick={resetCsvDialog} data-testid="csv-result-close-btn">
                  Done
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Members;